import React, { createContext, useContext, useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useLogin } from "../../../hooks/useAuthMutations";
import { logger } from "../../../utils/logger";
import api from "../../../config/axios";
import websocketService from "../../../services/websocket";
import {
  clearAccessToken,
  setAccessToken,
} from "../services/authTokenStore";

export interface User {
  id: number;
  username: string;
  email: string;
  nombre: string;
  apellido: string;
  rol: string;
  rol_nombre: string;
  activo: boolean;
  fecha_creacion?: string;
  ultimo_login?: string;
  requiere_cambio_password: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<User | null>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiereCambioPassword: boolean;
}

const defaultContext: AuthContextType = {
  user: null,
  token: null,
  login: async () => null,
  logout: () => {
    // Método por defecto - será sobrescrito por el provider
  },
  isAuthenticated: false,
  isLoading: true,
  requiereCambioPassword: false,
};

const AuthContext = createContext(defaultContext);

const getApiErrorStatus = (error: unknown): number | undefined => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }
  return undefined;
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response &&
    error.response.data &&
    typeof error.response.data === "object" &&
    "error" in error.response.data &&
    typeof error.response.data.error === "string"
  ) {
    return error.response.data.error;
  }
  return fallback;
};

const hasRefreshCsrfCookie = () =>
  document.cookie
    .split("; ")
    .some((cookie) => cookie.startsWith("csrf_refresh_token="));

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // React Query mutation
  const loginMutation = useLogin();

  useEffect(() => {
    const restoreSession = async () => {
      localStorage.removeItem("token");

      if (!hasRefreshCsrfCookie()) {
        localStorage.removeItem("user");
        clearAccessToken();
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.post("/auth/refresh");
        const { access_token, usuario } = response.data || {};

        if (access_token && usuario) {
          setAccessToken(access_token);
          setToken(access_token);
          setUser(usuario as User);
          return;
        }

        localStorage.removeItem("user");
        clearAccessToken();
      } catch (error) {
        logger.debug("No hay sesión persistente para restaurar:", error);
        localStorage.removeItem("user");
        clearAccessToken();
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await loginMutation.mutateAsync({ email, password });
      const { access_token, usuario, requiere_cambio_password } = response;

      setAccessToken(access_token);
      setToken(access_token);
      const authenticatedUser = usuario as User;
      setUser(authenticatedUser);
      // Nota: El header de Authorization se maneja en el interceptor de axios

      // Mostrar alerta si requiere cambio de contraseña
      if (requiere_cambio_password) {
        logger.warn("El usuario requiere cambiar su contraseña");
      }

      return authenticatedUser;
    } catch (error: unknown) {
      logger.error("Error en login:", error);

      // Manejar errores específicos
      const status = getApiErrorStatus(error);
      if (status === 423) {
        // Usuario bloqueado
        throw new Error(getApiErrorMessage(error, "Usuario bloqueado"));
      } else if (status === 401) {
        // Credenciales inválidas
        throw new Error("Credenciales inválidas");
      } else if (status === 429) {
        // Rate limit
        throw new Error(
          "Demasiados intentos. Intente nuevamente en 5 minutos."
        );
      } else {
        throw new Error("Error al intentar iniciar sesión");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    api.post("/logout").catch((error) => {
      logger.warn("No se pudo cerrar sesión en backend:", error);
    });

    setToken(null);
    setUser(null);
    clearAccessToken();
    // Sin sesión no se escuchan más avisos en tiempo real.
    websocketService.disconnect();

    // Limpiar localStorage
    localStorage.removeItem("user");
    localStorage.removeItem("lastPath");

    // Limpiar el token de axios (se maneja en el interceptor)
    // delete api.defaults.headers.common["Authorization"];
  };

  // Componente de carga
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100dvh",
          width: "100vw",
          background:
            "linear-gradient(-45deg, #2196f3, #21cbf3, #1de9b6, #a7ffeb)",
          backgroundSize: "400% 400%",
          animation: "gradientShift 15s ease infinite",
          "@keyframes gradientShift": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <CircularProgress size={60} sx={{ color: "white" }} />
      </Box>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token,
        isLoading,
        requiereCambioPassword: user?.requiere_cambio_password || false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};
